import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';

const ImageUploadSection = styled.div`
  margin-bottom: var(--spacing-6);
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-4);
`;

const ImageCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  background: #f8f9fa;
  border: 2px dashed var(--color-border);
  
  &.has-image {
    border: 2px solid var(--color-secondary);
    background: white;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover .overlay {
    opacity: 1;
  }
  
  .overlay button {
    background: white;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.1);
    }
    
    &.delete {
      background: #dc2626;
      color: white;
    }
    
    &.primary {
      background: var(--color-secondary);
      color: white;
    }
  }
`;

const UploadButton = styled.button`
  width: 100%;
  aspect-ratio: 1;
  border: 2px dashed var(--color-border);
  background: #f8f9fa;
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  transition: all 0.3s ease;
  color: var(--color-text-light);
  
  &:hover {
    border-color: var(--color-secondary);
    background: #f0f9ff;
    color: var(--color-secondary);
  }
  
  .icon {
    font-size: 2rem;
  }
  
  .text {
    font-size: var(--font-size-sm);
    font-weight: 500;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-lg);
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--color-border);
    border-top: 2px solid var(--color-secondary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-error);
  font-size: var(--font-size-sm);
  margin-top: var(--spacing-2);
  padding: var(--spacing-2);
  background: #fee2e2;
  border-radius: var(--border-radius);
`;

export interface ProductImage {
  id?: number;
  url: string;
  alt_text: string;
  is_primary: boolean;
  file?: File;
  uploading?: boolean;
}

interface ImageUploadProps {
  images: ProductImage[];
  onImagesChange: (images: ProductImage[] | ((prev: ProductImage[]) => ProductImage[])) => void;
  maxImages?: number;
  label?: string;
  entityLabel?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images = [],
  onImagesChange,
  maxImages = 6,
  label = 'Produktbilder',
  entityLabel = 'produkt'
}) => {
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setUploadError('');
    
    if (files.length === 0) return;
    
    // Check file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setUploadError('Endast JPEG, PNG och WebP bilder är tillåtna.');
      return;
    }
    
    // Check file sizes (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setUploadError('Filer får inte vara större än 5MB.');
      return;
    }
    
    // Check total image count
    if (images.length + files.length > maxImages) {
      setUploadError(`Du kan bara ladda upp max ${maxImages} bild${maxImages > 1 ? 'er' : ''} per ${entityLabel}.`);
      return;
    }

    // Process files
    const newImages: ProductImage[] = [];
    
    for (const file of files) {
      try {
        // Create preview
        const previewUrl = await createImagePreview(file);
        
        const newImage: ProductImage = {
          url: previewUrl,
          alt_text: file.name.replace(/\.[^/.]+$/, ''),
          is_primary: images.length === 0 && newImages.length === 0,
          file,
          uploading: true
        };
        
        newImages.push(newImage);
      } catch (error) {
        console.error('Error creating preview:', error);
      }
    }
    
    // Add new images to the list
    onImagesChange([...images, ...newImages]);
    
    // Upload images
    uploadImages(newImages);
    
    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const createImagePreview = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadImages = async (imagesToUpload: ProductImage[]) => {
    for (const image of imagesToUpload) {
      if (!image.file) continue;
      
      try {
        console.log('Starting upload for image:', image.file.name, 'Size:', image.file.size);
        
        // Resize image before upload
        const resizedFile = await resizeImage(image.file, 800, 800, 0.9);
        console.log('Resized image:', resizedFile.name, 'New size:', resizedFile.size);
        
        // Upload to API using centralized service
        console.log('Starting upload for image:', image.file.name, 'Size:', resizedFile.size);
        
        const result = await apiService.uploadImage(resizedFile);
        console.log('Upload successful, result:', result);
        
        // Update the specific image with server URL using callback pattern
        onImagesChange((currentImages: ProductImage[]) => {
          console.log('Upload completed, current images:', currentImages);
          console.log('Updating image:', image);
          const updatedImages = currentImages.map((img: ProductImage) => 
            img === image ? { 
              ...img, 
              uploading: false, 
              url: result.url.startsWith('http') ? result.url : `https://lillaflora.nu${result.url}` 
            } : img
          );
          console.log('Updated images after upload:', updatedImages);
          return updatedImages;
        });
        
      } catch (error) {
        console.error('Upload failed for image:', image.file?.name, 'Error details:', error);
        console.error('Error stack:', (error as Error).stack);
        setUploadError(`Uppladdning misslyckades för ${image.file?.name}: ${(error as Error).message}`);
        
        // Remove failed image using callback pattern
        onImagesChange((currentImages: ProductImage[]) => currentImages.filter((img: ProductImage) => img !== image));
      }
    }
  };

  const resizeImage = (file: File, maxWidth: number, maxHeight: number, quality: number): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            resolve(file);
          }
        }, file.type, quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };


  const handleDelete = (imageToDelete: ProductImage) => {
    const filteredImages = images.filter(img => img !== imageToDelete);
    onImagesChange(filteredImages);
  };

  const handleSetPrimary = (imageToSet: ProductImage) => {
    const updatedImages = images.map(img => ({
      ...img,
      is_primary: img === imageToSet
    }));
    onImagesChange(updatedImages);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <ImageUploadSection>
      <label style={{ display: 'block', marginBottom: 'var(--spacing-2)', fontWeight: '500' }}>
        {label} ({images.length}/{maxImages})
      </label>
      
      <ImageGrid>
        {images.map((image, index) => (
          <ImageCard key={index} className="has-image">
            <img src={image.url} alt={image.alt_text} />
            
            {image.uploading && (
              <LoadingOverlay>
                <div className="spinner"></div>
              </LoadingOverlay>
            )}
            
            {!image.uploading && (
              <div className="overlay">
                <button
                  type="button"
                  className={image.is_primary ? "primary" : ""}
                  onClick={() => handleSetPrimary(image)}
                  title={image.is_primary ? "Huvudbild" : "Sätt som huvudbild"}
                >
                  ⭐
                </button>
                <button
                  type="button"
                  className="delete"
                  onClick={() => handleDelete(image)}
                  title="Ta bort bild"
                >
                  🗑️
                </button>
              </div>
            )}
          </ImageCard>
        ))}
        
        {images.length < maxImages && (
          <UploadButton type="button" onClick={handleUploadClick}>
            <div className="icon">📷</div>
            <div className="text">Lägg till bild</div>
          </UploadButton>
        )}
      </ImageGrid>
      
      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple={maxImages > 1}
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
      />
      
      {uploadError && (
        <ErrorMessage>{uploadError}</ErrorMessage>
      )}
      
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)', marginTop: 'var(--spacing-2)' }}>
        Tillåtna format: JPEG, PNG, WebP • Max filstorlek: 5MB • Max antal: {maxImages} bilder
        <br />
        Bilder ändras automatiskt till optimala storlekar för webben.
      </div>
    </ImageUploadSection>
  );
};

export default ImageUpload;
